import axios from 'axios';
import { getSessionUUID } from '@utils/uuid';
import { logger, emitDebugLog } from '@utils/logger';

const API_CONFIG = {
  baseURL: 'https://demo-build-com.b.lucidworks.cloud',
  application: 'Build_3',
  queryProfile: 'ui',
  apiKey: '2f50ed2f-c373-4e3a-a487-5bfcba105672',
  timeout: 10000,
};

const QUERY_PROFILES = {
  'default': 'Build_3',
  'typeahead': 'build_3_typeahead',
  'suggestions': 'build_3_typeahead',
  'landing-page': 'build_3_landing',
  'pdp': 'build_3_pdp',
  'category': 'build_3_category',
};

const DEFAULT_PARAMS = {
  query: '*:*',
  rows: 12,
  start: 0
};

const ERROR_MESSAGES = {
  401: 'Authentication failed. Please check your API credentials.',
  403: 'You don\'t have permission to access this resource.',
  404: 'The requested resource was not found.',
  429: 'Too many requests. Please try again later.',
  500: 'Server error. Please try again later.',
  network: 'Network error. Please check your connection.'
};

const TYPEAHEAD_FIELDS = [
  'id', 'title', 'name', 'display_name_s', 'text', 'description', 'description_t',
  'url', 'type', 'image_url', 'image_url_s', 'image', 'imageuri', 'images', 'variant_images'
];

// Cache for session and user IDs
let cachedSessionId = null;
const getUserId = () => 'anonymous';
const getSessionId = () => {
  if (!cachedSessionId) {
    cachedSessionId = getSessionUUID();
  }
  return cachedSessionId;
};

// Log API configuration on module load
logger.debug('🔧 API Configuration loaded:', {
  baseURL: API_CONFIG.baseURL,
  application: API_CONFIG.application,
  queryProfile: API_CONFIG.queryProfile,
  timeout: API_CONFIG.timeout,
  apiKeyExists: !!API_CONFIG.apiKey
});

const validateConfig = () => {
  const required = ['baseURL', 'application', 'queryProfile', 'apiKey'];
  const missing = required.filter(key => !API_CONFIG[key]);
  if (missing.length > 0) {
    logger.error(`Missing required API configuration: ${missing.join(', ')}`);
    logger.error('Current API config:', API_CONFIG);
    return false;
  }
  logger.debug('✅ API configuration validated successfully');
  return true;
};

const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_CONFIG.apiKey
  }
});

// Optimized error handler
const handleApiError = (error) => {
  const status = error.response?.status;
  const isServerError = status >= 500 && status <= 504;
  
  if (!error.response) {
    logger.error('Network error - no response from server', error);
    return new Error(ERROR_MESSAGES.network);
  }
  
  const errorMessage = ERROR_MESSAGES[status] || 
    (isServerError ? ERROR_MESSAGES[500] : error.message);
  
  logger.error(`${status} error occurred`, error);
  return new Error(errorMessage);
};

apiClient.interceptors.response.use(
  response => {
    logger.debug('✅ HTTP Response received:', {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      method: response.config.method?.toUpperCase()
    });
    return response;
  },
  error => {
    logger.error('❌ HTTP Response error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      message: error.message
    });
    return Promise.reject(handleApiError(error));
  }
);

// Optimized parameter building
const buildSearchParams = (searchOptions) => {
  const {
    query, start, rows, filterQueries = [],
    collapse, collapseField, expand, expandField, fields,
    facet, facetLimit, facetMinCount, sort
  } = searchOptions;

  const params = new URLSearchParams({
    q: query,
    start: start.toString(),
    rows: rows.toString(),
    user_id: getUserId(),
    session: getSessionId()
  });

  // Add sort parameter if specified
  if (sort && sort !== 'score desc') {
    params.append('sort', sort);
  }

  // Add filter queries
  filterQueries.forEach(fq => params.append('fq', fq));

  // Add facet parameters
  if (facet) {
    params.append('facet', 'true');
    if (facetLimit) params.append('facet.limit', facetLimit.toString());
    if (facetMinCount) params.append('facet.mincount', facetMinCount.toString());
  }

  // Add collapse/expand parameters
  if (collapse) {
    params.append('fq', `{!collapse field=${collapseField}}`);
    if (expand) {
      params.append('expand', 'true');
      params.append('expand.field', collapseField);
      if (expandField) {
        params.append('expand.fl', expandField);
      }
    }
  }

  // Add fields if specified
  if (fields) {
    params.append('fl', Array.isArray(fields) ? fields.join(',') : fields);
  }

  return params;
};

// Optimized query processing
const processQuery = (query, id) => {
  if (id && !query.includes('id:')) {
    const escapedId = id.replace(/([!:+&|<>=^(){}[\]^"~*?\\/-])/g, '\\$1');
    return `id:"${escapedId}"`;
  }
  return query;
};

// Optimized result processing
const processSearchResult = (response, id, startTime) => {
  const elapsedMs = Date.now() - startTime;
  let documents = response.data.response?.docs || [];

  if (id) {
    const originalCount = documents.length;
    documents = documents.filter(doc => doc.id === id);
    logger.info('🔍 Filtered documents by ID:', { originalCount, filteredCount: documents.length, targetId: id });
  }

  return {
    success: true,
    data: response.data,
    totalResults: response.data.response?.numFound || 0,
    documents: documents,
    facets: response.data.facet_counts || null,
    responseTime: response.data.responseHeader?.QTime || 0,
    elapsedTime: elapsedMs
  };
};

export const search = async (options = {}) => {
  const searchOptions = { ...DEFAULT_PARAMS, ...options };
  const { query, rows, start, filterQueries, queryProfile, signal, id, sort } = searchOptions;
  
  logger.debug('[API][search] request:', { query, rows, start, filterQueries, queryProfile, id, sort });
  logger.group('🔍 Search API Call');
  logger.info('Request parameters:', searchOptions);
  
  if (!validateConfig()) {
    const errorMsg = 'API configuration is incomplete';
    logger.error('❌ Configuration validation failed:', errorMsg);
    emitDebugLog(`❌ ${errorMsg}`);
    logger.groupEnd();
    return { success: false, error: errorMsg, data: null };
  }
  
  const startTime = Date.now();
  
  try {
    const processedQuery = processQuery(query, id);
    const profileEndpoint = QUERY_PROFILES[queryProfile] || QUERY_PROFILES.default;
    
    logger.info('🎯 Using query profile:', { queryProfile, profileEndpoint });
    
    const params = buildSearchParams({ ...searchOptions, query: processedQuery });
    const requestUrl = `/api/apps/${API_CONFIG.application}/query/${profileEndpoint}`;
    
    logger.info('🌐 Making request to:', `${API_CONFIG.baseURL}${requestUrl}`);
    if (sort) {
      logger.info('📊 Sort parameter:', sort);
    }
    emitDebugLog(`🌐 Request: ${API_CONFIG.baseURL}${requestUrl}${sort ? ` (sorted by ${sort})` : ''}`);
    
    const response = await apiClient.get(requestUrl, { params, signal });
    const result = processSearchResult(response, id, startTime);
    
    logger.info('✅ Search completed successfully:', {
      totalResults: result.totalResults,
      documentsReturned: result.documents.length,
      responseTime: result.responseTime,
      elapsedTime: result.elapsedTime,
      hasFacets: !!result.facets,
      sortApplied: sort || 'default'
    });
    
    emitDebugLog(`✅ Response: ${result.totalResults} results in ${result.elapsedTime}ms${sort ? ` (sorted by ${sort})` : ''}`);
    logger.groupEnd();
    return result;
    
  } catch (error) {
    const isAborted = error.name === 'AbortError' || error.name === 'CanceledError' || axios.isCancel(error);
    const errorMessage = isAborted ? 'Search was cancelled' : error.message;
    
    logger.error('❌ Search API error occurred:', { message: errorMessage, isAborted });
    emitDebugLog(`❌ Error: ${errorMessage}`);
    logger.groupEnd();
    
    return {
      success: false,
      error: errorMessage,
      data: null,
      cancelled: isAborted
    };
  }
};

export const getTypeaheadSuggestions = async (query, signal = null) => {
  logger.group('🔤 Typeahead Suggestions API Call');
  logger.info('Typeahead request:', { query, queryLength: query?.length });
  
  if (!query || query.trim().length < 2) {
    logger.warn('❌ Query too short for typeahead:', { query, length: query?.length });
    logger.groupEnd();
    return { success: false, error: 'Query too short', suggestions: [] };
  }
  
  try {
    logger.info('🔍 Making typeahead search request...');
    const result = await search({
      query: `${query}*`,
      rows: 10,
      queryProfile: 'typeahead',
      signal,
      fields: TYPEAHEAD_FIELDS
    });
    
    if (!result.success) {
      logger.error('❌ Typeahead search failed:', result.error);
      logger.groupEnd();
      return { ...result, suggestions: [] };
    }
    
    logger.info('📝 Processing typeahead results:', { documentsCount: result.documents?.length });
    
    const suggestions = result.documents.map((doc, index) => {
      const getImageUrl = () => {
        const imageFields = ['image_url', 'image_url_s', 'image', 'imageuri'];
        for (const field of imageFields) {
          if (doc[field]) return doc[field];
        }
        if (Array.isArray(doc.images) && doc.images[0]) return doc.images[0];
        if (Array.isArray(doc.variant_images) && doc.variant_images[0]) return doc.variant_images[0];
        return null;
      };

      return {
        id: doc.id || `suggestion_${index}`,
        title: doc.title || doc.name || doc.display_name_s || doc.text || 'Untitled',
        description: doc.description || doc.description_t || doc.content?.substring(0, 100) || '',
        url: doc.url || null,
        type: doc.type || 'product',
        image: getImageUrl(),
        originalDoc: doc
      };
    });
    
    logger.info('✅ Typeahead suggestions processed:', { 
      suggestionsCount: suggestions.length,
      withImages: suggestions.filter(s => s.image).length 
    });
    logger.groupEnd();
    
    return {
      success: true,
      suggestions,
      data: result.data
    };
  } catch (error) {
    logger.error('❌ Typeahead error:', { message: error.message, query });
    logger.groupEnd();
    return {
      success: false,
      error: error.message,
      suggestions: []
    };
  }
};

export const facetedSearch = (searchParams = {}, signal = null) => {
  logger.debug('[API][facetedSearch] called with:', { searchParams, signal });
  logger.group('🔍 Faceted Search API Call');
  logger.info('Faceted search parameters:', searchParams);
  
  const result = search({
    ...searchParams,
    facet: true,
    signal
  });
  
  logger.groupEnd();
  return result;
};

export const getPage = (lastSearch = {}, page = 1, signal = null) => {
  logger.debug('[API][getPage] called with:', { lastSearch, page, signal });
  logger.group('📄 Get Page API Call');
  
  const rows = lastSearch.rows || DEFAULT_PARAMS.rows;
  const start = (page - 1) * rows;
  
  logger.info('Pagination parameters:', { 
    page, 
    rows, 
    start, 
    lastSearchParams: Object.keys(lastSearch) 
  });
  
  const result = search({
    ...lastSearch,
    start,
    signal
  });
  
  logger.groupEnd();
  return result;
};

export const getHomePageProducts = (limit = 12, signal = null) => {
  logger.group('🏠 Homepage Products API Call');
  logger.info('Homepage products request:', { limit });
  
  const result = search({
    query: '*:*',
    rows: limit,
    queryProfile: 'landing-page',
    signal
  });
  
  logger.groupEnd();
  return result;
};

const searchApi = {
  search,
  facetedSearch,
  getPage,
  getTypeaheadSuggestions,
  getHomePageProducts
};

export default searchApi;
